import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Share2, Plus, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API, api, errMsg } from "@/lib/api";
import { COMPANY } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const digits = (s) => (s || "").replace(/\D/g, "");

const ContactsEditor = ({ contacts, setContacts, selected, setSelected }) => {
  const { can } = useAuth();
  const editable = can("settings");
  const [label, setLabel] = useState("");
  const [phone, setPhone] = useState("");
  const persist = async (next) => {
    try {
      const { data } = await api.put("/settings", { contacts: next });
      setContacts(data.contacts);
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const add = async () => {
    const p = digits(phone);
    if (!label.trim() || p.length < 8 || p.length > 15) return toast.error("أدخل الاسم والرقم مع رمز الدولة (8 إلى 15 رقماً)");
    if (await persist([...contacts, { label: label.trim(), phone: p }])) {
      setSelected(p);
      setLabel("");
      setPhone("");
    }
  };
  const remove = async (i) => {
    const next = contacts.filter((_, j) => j !== i);
    if ((await persist(next)) && !next.some((c) => c.phone === selected)) setSelected(next[0]?.phone || "");
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2" data-testid="share-contacts">
        {contacts.map((c, i) => (
          <span key={c.phone + i} className={`h-10 ps-3 pe-1 rounded-full border flex items-center gap-1 text-sm ${selected === c.phone ? "bg-brand-navy text-white border-brand-navy" : "border-slate-200"}`}>
            <button onClick={() => setSelected(c.phone)} data-testid={`share-contact-${i}`} className="font-semibold">{c.label} <span className="font-mono text-xs opacity-75" dir="ltr">{c.phone}</span></button>
            {editable && <button onClick={() => remove(i)} aria-label="حذف" data-testid={`share-contact-remove-${i}`} className="h-8 w-8 grid place-items-center rounded-full hover:bg-black/10"><X size={14} /></button>}
          </span>
        ))}
        {contacts.length === 0 && <p className="text-sm text-slate-500">لا توجد جهات محفوظة. أضف رقم الورشة أو المبيعات.</p>}
      </div>
      {editable && <div className="flex gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="الجهة (الورشة / المبيعات)" className="h-10" data-testid="share-new-label" />
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="رقم مع رمز الدولة" dir="ltr" className="h-10 text-left" data-testid="share-new-phone" />
        <button onClick={add} data-testid="share-add-contact" aria-label="إضافة" className="h-10 w-10 shrink-0 grid place-items-center rounded-lg border border-slate-200 hover:bg-slate-100"><Plus size={16} /></button>
      </div>}
    </div>
  );
};

export const ShareWhatsAppDialog = ({ open, onClose, insp }) => {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const c = insp.customer;
  const m = insp.measurements || {};
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/settings").then((r) => {
      setContacts(r.data.contacts);
      if (r.data.contacts[0]) setSelected(r.data.contacts[0].phone);
    });
    setMsg(`${COMPANY}\nكشف معاينة ومقاسات ${insp.ref}\nالعميل: ${c.name}${c.area ? ` - ${c.area}` : ""}\nالمقاس: ${m.width || "-"} × ${m.height || "-"} مم · ${m.door_type || "-"}`);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendWhatsApp = async () => {
    if (!selected) return toast.error("اختر جهة الإرسال");
    setBusy(true);
    const win = window.open("", "_blank");
    try {
      const { data } = await api.post(`/inspections/${insp.id}/share`);
      const link = `${API.replace(/\/api$/, "")}${data.path}`;
      const url = `https://wa.me/${selected}?text=${encodeURIComponent(`${msg}\n\nتقرير PDF (صالح 7 أيام):\n${link}`)}`;
      if (win) win.location.href = url;
      else window.location.href = url;
    } catch (e) {
      win?.close();
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const shareFile = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/inspections/${insp.id}/pdf`, { responseType: "blob" });
      const file = new File([data], `${insp.ref}.pdf`, { type: "application/pdf" });
      await navigator.share({ files: [file], text: msg });
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("تعذرت مشاركة الملف");
    } finally {
      setBusy(false);
    }
  };

  const canShareFiles = typeof navigator !== "undefined" && navigator.canShare?.({ files: [new File([""], "a.pdf", { type: "application/pdf" })] });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="share-whatsapp-dialog">
        <DialogHeader><DialogTitle className="text-start font-heading">إرسال التقرير عبر واتساب</DialogTitle></DialogHeader>
        <ContactsEditor contacts={contacts} setContacts={setContacts} selected={selected} setSelected={setSelected} />
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={5} data-testid="share-message-input" />
        <button onClick={sendWhatsApp} disabled={busy} data-testid="share-send-whatsapp"
          className="h-12 rounded-lg bg-[#25D366] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#1EB257] disabled:opacity-60">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <MessageCircle size={18} />}إرسال لرقم الجهة المختارة (رابط التقرير)
        </button>
        {canShareFiles && (
          <button onClick={shareFile} disabled={busy} data-testid="share-file-native"
            className="h-12 rounded-lg border border-slate-200 font-bold flex items-center justify-center gap-2 hover:bg-slate-100">
            <Share2 size={18} />مشاركة ملف PDF مباشرة (اختر واتساب)
          </button>
        )}
        <p className="text-xs text-slate-500">يُرسل رابط آمن لتحميل التقرير ينتهي بعد 7 أيام. من الجوال يمكنك مشاركة ملف PDF نفسه.</p>
      </DialogContent>
    </Dialog>
  );
};
